import React, { useContext, useEffect, useState } from 'react';
import { View } from 'react-native';
import Constants from 'expo-constants';
import { WebView } from 'react-native-webview';
import * as ExpoFileLogger from 'expo-file-logger';
import { StyledWebView } from '../common/StyledWebView';
import { H1, H6 } from '../text';
import { default as AppConstants } from '../../common/Constants';
import AppContext, { AppActions } from '../../contexts/App';
import LayoutContext from '../../contexts/Layout';

const STYLED_WEB_VIEW_ID = 'videoPlayer';

const TITLE_LOCATION_OPTIONS = Object.freeze({
    TITLE_UPPER_LEFT: 'Upper Left Corner',
    TITLE_LOWER_LEFT: 'Lower Left Corner',
    TITLE_BELOW_VIDEO: 'Below Video',
    TITLE_ABOVE_VIDEO: 'Above Video'
});

const SARDIUS_VJS_CONTROL_BAR_HEIGHT = 50; // docment.querySelector('.vjs-control-bar').offsetHeight @jacksonkr

const Video = props => {
    const {
        state: { action, settings }
    } = useContext(AppContext);
    const { layout } = useContext(LayoutContext);
    const [injectedPage, setInjectedPage] = useState();
    const [service, setService] = useState(action?.data?.service);
    const dataSource = injectedPage || action.data.service || props;
    const textColor = dataSource?.textColor || '#000';
    const title = dataSource?.title;
    const subtitle = dataSource?.subtitle;
    const description = dataSource?.description;
    const briefDescription = dataSource?.briefescription;
    const titleLocation = dataSource?.titleLocation;
    const youTubeVideoCode = dataSource?.youtubeVideoCode;
    const haiVisionPublicID = dataSource?.haiVisionPublicID || dataSource?.haivisionPublicId;
    const useAdPolicy = dataSource?.useAdPolicy;
    const nodeTypeAlias = dataSource?.nodeTypeAlias || 'VideoPlayer';

    let player;
    let component;

    useEffect(() => {
        const asyncEffect = async () => {
            const carouselTitleMatch = window.location.href.match(/sermon-carousel\/([\w-_]+)[\/\?]?/);
            if (carouselTitleMatch && carouselTitleMatch[1]) return; // do nothing if there's a title

            const params = new URLSearchParams(window.location.search);
            const source = params.get('source');
            if (!source) return;

            if (!service) {
                const response = await window.fetch(
                    `${Constants.expoConfig.extra.baseUrl}${AppConstants.REACT_APP_PAGE_BASE}${AppConstants.REACT_APP_INTERNAL_REDIRECT_FEATURED}`
                );
                const json = await response.json();

                if (json) setService({ haivisionPublicId: source }); // cheaper that doing a new dispatch @jacksonkr
            }
        };

        asyncEffect();
    }, [service]);

    useEffect(() => {
        let unmount = true;
        if (unmount) {
            if (action.data.service) {
                const playerPage = Object.values(action.data.page.contentItems ?? {});
                setInjectedPage(playerPage[0]);
                setService(action.data.service);
            } else {
                const serviceKey = action?.href?.replaceAll('?source=', '');

                if (serviceKey) {
                    (async () => {
                        const queryString = `?domain=${settings.streamingSettings.domain}&key=${serviceKey}&documentType=serviceVideo`;

                        try {
                            const response = await window.fetch(`${Constants.manifest?.extra?.baseUrl}${AppConstants.REACT_APP_DOMAIN_ITEM_BY_KEY_BASE}${queryString}`);
                            if (!response?.ok) {
                                return null;
                            }
                            const json = await response.json();

                            if (!json.status.equals('404')) {
                                setService(json.item);
                            }
                        } catch (e) {
                            ExpoFileLogger.log('error', `Error fetching service video: ${e}`);
                        }
                    })();
                }
            }
        }

        return () => (unmount = false);
    }, [action.data.page.contentItems, action.data.service, action?.href, settings.streamingSettings.domain]);

    if (youTubeVideoCode) {
        player = `<iframe src="https://www.youtube.com/embed/${youTubeVideoCode}" 
		                  style="display: block; height: 100%; width: 100%;" 
						  title="${title}" 
						  allow="fullscreen">
				  </iframe>`;
    } else {
        const haiVisionPublicId = dataSource?.haivisionPublicId;
        let src;

        if (service) {
            src = useAdPolicy ? settings.vcmsSettings.onDemandPlayerPR + service.haivisionPublicId : settings.vcmsSettings.onDemandPlayer + service.haivisionPublicId;
        } else if (haiVisionPublicID || haiVisionPublicId) {
            const id = haiVisionPublicID || haiVisionPublicId;
            src = useAdPolicy && haiVisionPublicId ? settings.vcmsSettings.onDemandPlayerPR + id : settings.vcmsSettings.onDemandPlayer + id;
        }

        if (src) {
            player = `<iframe allowfullscreen="0"
                              loading="lazy"
					  		  frameBorder="0"
					  		  scrolling="no"
							  id="${title}"
					  		  seamless="seamless"
					  		  src="${src}" 
                              style="display: block; height: 100%; width: 100%;" 
					  		  title="${title}" 
					  		  donotallowfullscreen>
					  </iframe>`;
        }
    }

    if (titleLocation?.equals(TITLE_LOCATION_OPTIONS.TITLE_ABOVE_VIDEO)) {
        component = (
            <View>
                <View style={{ marginLeft: layout.styleGuide.spacer1, flexWrap: 'wrap' }} dataSet={{ spacerLeft: `StyleGuide.Spacer1(${layout.styleGuide.spacer1})` }}>
                    {!!title && <H1 text={title} style={{ color: textColor }} />}
                    {!!subtitle && <H6 text={subtitle} style={{ color: textColor }} />}
                </View>
                <View style={{ flexDirection: 'row' }} testID='TITLE_LOCATION_OPTIONS.TITLE_ABOVE_VIDEO'>
                    {layout.device.os.equals('web') && <div dangerouslySetInnerHTML={{ __html: player }} style={{ aspectRatio: '16/9', width: '100%' }} />}
                    {!layout.device.os.equals('web') && (
                        <WebView
                            allowsInlineMediaPlayback={true}
                            containerStyle={{ height: '100%', width: '100%' }}
                            scrollEnabled={false}
                            source={{ html: player }}
                            webviewDebuggingEnabled={__DEV__}
                        />
                    )}
                </View>
                {!!description && (
                    <StyledWebView
                        html={description}
                        textProps={{
                            style: { margin: layout.styleGuide.spacer1 }
                        }}
                        dataSet={{
                            spacerBottom: `StyleGuide.Spacer(${layout.styleGuide.spacer1})`,
                            spacerLeft: `StyleGuide.Spacer(${layout.styleGuide.spacer1})`,
                            spacerRight: `StyleGuide.Spacer(${layout.styleGuide.spacer1})`,
                            spacerTop: `StyleGuide.Spacer(${layout.styleGuide.spacer1})`
                        }}
                        nodeId={STYLED_WEB_VIEW_ID}
                    />
                )}
                {!!briefDescription && (
                    <StyledWebView
                        html={briefDescription}
                        textProps={{
                            style: { margin: layout.styleGuide.spacer1 }
                        }}
                        dataSet={{
                            spacerBottom: `StyleGuide.Spacer(${layout.styleGuide.spacer1})`,
                            spacerLeft: `StyleGuide.Spacer(${layout.styleGuide.spacer1})`,
                            spacerRight: `StyleGuide.Spacer(${layout.styleGuide.spacer1})`,
                            spacerTop: `StyleGuide.Spacer(${layout.styleGuide.spacer1})`
                        }}
                        nodeId={STYLED_WEB_VIEW_ID}
                    />
                )}
            </View>
        );
    } else if (titleLocation?.equals(TITLE_LOCATION_OPTIONS.TITLE_BELOW_VIDEO)) {
        component = (
            <View>
                <View style={{ flexDirection: 'row', height: layout.device.os.equals('web') ? undefined : layout.dimensions.height, width: props.width }}>
                    {layout.device.os.equals('web') && <div dangerouslySetInnerHTML={{ __html: player }} style={{ aspectRatio: '16/9', width: '100%' }} />}
                    {!layout.device.os.equals('web') && (
                        <WebView
                            allowsInlineMediaPlayback={true}
                            containerStyle={{ height: '100%', width: '100%' }}
                            scrollEnabled={false}
                            source={{ html: player }}
                            webviewDebuggingEnabled={__DEV__}
                        />
                    )}
                </View>
                <View
                    style={{
                        flexWrap: 'wrap',
                        marginLeft: layout.styleGuide.spacer1,
                        marginTop: layout.styleGuide.spacer4
                    }}
                    dataSet={{ spacerLeft: `StyleGuide.Spacer1(${layout.styleGuide.spacer1})`, spacerTop: `StyleGuide.Spacer4(${layout.styleGuide.spacer4})` }}
                >
                    {!!title && <H1 text={title} style={{ color: textColor }} />}
                    {!!subtitle && <H6 text={subtitle} style={{ color: textColor }} />}
                </View>
                {!!description && (
                    <StyledWebView
                        html={description}
                        textProps={{
                            style: { margin: layout.styleGuide.spacer1 }
                        }}
                        dataSet={{
                            spacerBottom: `StyleGuide.Spacer(${layout.styleGuide.spacer1})`,
                            spacerLeft: `StyleGuide.Spacer(${layout.styleGuide.spacer1})`,
                            spacerRight: `StyleGuide.Spacer(${layout.styleGuide.spacer1})`,
                            spacerTop: `StyleGuide.Spacer(${layout.styleGuide.spacer1})`
                        }}
                        nodeId={STYLED_WEB_VIEW_ID}
                    />
                )}
                {!!briefDescription && (
                    <StyledWebView
                        html={briefDescription}
                        textProps={{
                            style: { margin: layout.styleGuide.spacer1 }
                        }}
                        dataSet={{
                            spacerBottom: `StyleGuide.Spacer(${layout.styleGuide.spacer1})`,
                            spacerLeft: `StyleGuide.Spacer(${layout.styleGuide.spacer1})`,
                            spacerRight: `StyleGuide.Spacer(${layout.styleGuide.spacer1})`,
                            spacerTop: `StyleGuide.Spacer(${layout.styleGuide.spacer1})`
                        }}
                        nodeId={STYLED_WEB_VIEW_ID}
                    />
                )}
            </View>
        );
    } else if (titleLocation?.equals(TITLE_LOCATION_OPTIONS.TITLE_LOWER_LEFT)) {
        component = (
            <View>
                <View
                    style={{ flexDirection: 'row', marginBottom: layout.styleGuide.componentSeparator }}
                    dataSet={{ spacerBottom: `StyleGuide.ComponentSeparator(${layout.styleGuide.componentSeparator})` }}
                >
                    {layout.device.os.equals('web') && <div dangerouslySetInnerHTML={{ __html: player }} style={{ aspectRatio: '16/9', width: '100%' }} />}
                    {!layout.device.os.equals('web') && (
                        <WebView
                            allowsInlineMediaPlayback={true}
                            containerStyle={{ height: '100%', width: '100%' }}
                            scrollEnabled={false}
                            source={{ html: player }}
                            webviewDebuggingEnabled={__DEV__}
                        />
                    )}
                    {(!!title || !!subtitle) && (
                        <View
                            style={{
                                bottom: SARDIUS_VJS_CONTROL_BAR_HEIGHT,
                                left: layout.styleGuide.spacer1,
                                position: 'absolute'
                            }}
                            dataSet={{ spacerLeft: `StyleGuide.Spacer1(${layout.styleGuide.spacer1})` }}
                        >
                            <View>
                                {!!title && <H1 style={{ color: textColor }} text={title} />}
                                {!!subtitle && <H6 style={{ color: textColor }} text={subtitle} />}
                            </View>
                        </View>
                    )}
                    {!!description && <StyledWebView html={description} nodeId={STYLED_WEB_VIEW_ID} />}
                    {!!briefDescription && <StyledWebView html={briefDescription} nodeId={STYLED_WEB_VIEW_ID} />}
                </View>
            </View>
        );
    } else {
        if (layout.device.os.equals('web')) {
            component = <div dangerouslySetInnerHTML={{ __html: player }} style={{ aspectRatio: '16/9', width: '100%' }} />;
        } else {
            component = (
                <WebView
                    allowsInlineMediaPlayback={true}
                    style={{ height: '100%', width: '100%' }}
                    scrollEnabled={false}
                    source={{ html: player }}
                    webviewDebuggingEnabled={__DEV__}
                />
            );
        }
    }

    if (!component) {
        return null;
    }

    return (
        <View
            dataSet={{ umbracoComponentName: nodeTypeAlias, spacerBottom: `StyleGuide.ComponentSeparator(${layout.styleGuide.componentSeparator})` }}
            style={{ marginBottom: layout.styleGuide.componentSeparator }}
        >
            {component}
        </View>
    );
};

export default Video;
